// ink-component
import React from 'react';
import Circle from './components/ShimmerCircle/ShimmerCircle';
import Text from './components/ShimmerText/ShimmerText';
import Rectangle from './components/ShimmerRectangle/ShimmerRectangle';

import Box from '../Box/Box';
import Column from '../Grid/Column/Column';
import HStack from '../HStack/HStack';
import Row from '../Grid/Row/Row';
import VStack from '../VStack/VStack';

export interface ShimmerProps {
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** You can create a custom shimmer by using <Shimmer.Circle />, <Shimmer.Rectangle />, or <Shimmer.Text />.
   * Alternatively, you can use the preset prop to choose a preset shape which corresponds to an Ink component.
   */
  preset?: 'checkbox' | 'content' | 'field-stacked' | 'field' | 'input' | 'radio' | 'selectable-list' | 'textarea';
}

/**
 * @purpose Provides user feedback to mitigate focus on the loading process
 * @group shimmer
 * @aka skeleton loading
 */
const Shimmer = (props: ShimmerProps) => {
  const { preset, 'data-testid': dataTestId } = props;

  switch (preset) {
    case 'checkbox':
      return (
        <Box top="10" data-testid={dataTestId}>
          <Rectangle width="20px" height="20px" />
        </Box>
      );
    case 'content':
      return (
        <VStack spacing="medium" data-testid={dataTestId}>
          <Rectangle width="200px" height="20px" />
          <Text rows={5} />
        </VStack>
      );
    case 'field-stacked':
      return (
        <VStack spacing="small" data-testid={dataTestId}>
          <Rectangle width="120px" height="20px" />
          <Rectangle width="300px" height="40px" />
        </VStack>
      );
    case 'field':
      return (
        <Row data-testid={dataTestId}>
          <Column mobile="4-of-12">
            <HStack align="right">
              <Box top="10" right="10">
                <Shimmer.Rectangle width="120px" height="20px" />
              </Box>
            </HStack>
          </Column>
          <Column mobile="8-of-12">
            <Shimmer.Rectangle width="300px" height="40px" />
          </Column>
        </Row>
      );
    case 'input':
      // @ts-ignore
      return <Rectangle width="300px" height="42px" data-testid={dataTestId} />;
    case 'radio':
      return <Circle size="20" data-testid={dataTestId} />;
    case 'selectable-list':
      return <Rectangle height="60px" data-testid={dataTestId} />;
    case 'textarea':
      return <Rectangle width="300px" height="120px" data-testid={dataTestId} />;
    default:
      return <Rectangle data-testid={dataTestId} />;
  }
};

Shimmer.defaultProps = {
  'data-testid': undefined,
  preset: undefined,
};

Shimmer.Circle = Circle;
Shimmer.Text = Text;
Shimmer.Rectangle = Rectangle;

export { ShimmerCircleProps } from './components/ShimmerCircle/ShimmerCircle';
export { ShimmerTextProps } from './components/ShimmerText/ShimmerText';
export { ShimmerRectangleProps } from './components/ShimmerRectangle/ShimmerRectangle';

Shimmer.displayName = 'Shimmer';

export default Shimmer;
